import { History, Pencil, Plus, Trash2 } from 'lucide-react';

import { openChatHistoryAtMessage } from '@/shared/stores';
import { useCallback, useMemo, useState } from 'react';

import { useCareStateCommands, useCareStateDomain } from '@/entities/care-state-domain';
import { ENV } from '@/shared/config';
import { formatLotusUuidShortDate } from '@/shared/lib';
import { ScopedTreeViewer } from '@/features/scoped-tree-viewer';
import type { ScopedListRow } from '@/features/scoped-tree-viewer';
import {
  AgentTweakableArea,
  ButtonTool,
  TextFieldTool,
  useAgentTweakable,
  useAgentVisible,
} from '@/features/copilot/agent-tweakable';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/shadcn/collapsible';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/shared/ui/shadcn/context-menu';
import { cn } from '@/lib/utils';
import type {
  CareStateRequestStatusT,
  CareStateRequestUrgencyT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import type { CareStateCommandMeta } from '@/entities/care-state-domain/commands/types';
import { ProposedActionsPlanView, useProposedActionsPlan } from '@/features/proposed-actions-plan';

import { CARE_STATE_FIRST_OPEN_REQUEST_ANCHOR } from '@/app/hints';

import { scopeToSelection, selectionToScope } from '../model/selection';
import { findNextOpenRequestInTreeOrder } from '../model/nextOpenRequest';
import { type RequestTabId } from '../model/requestTabs';
import { useCareStateScopeRows } from '../model/useCareStateScopeRows';
import { CareStateRequestRow } from './CareStateRequestRow';
import { RequestStatusTabs } from './RequestStatusTabs';

type RequestStatusT = CareStateRequestStatusT;

function CareStateScopeTreeAgentRowButton(props: {
  rowId: string;
  rowLabel: string;
  onSelectRowId: (rowId: string) => void;
}) {
  const { rowId, rowLabel, onSelectRowId } = props;
  const safeLabel = rowLabel.trim().replace(/\s+/g, ' ').replaceAll('.', '_');
  const agentKey = `select:${safeLabel}__${rowId}`;

  const onClick = useCallback(
    (v: boolean) => {
      if (v !== true) return null;
      onSelectRowId(rowId);
      return { selectedRowId: rowId, selectedTitle: rowLabel };
    },
    [onSelectRowId, rowId, rowLabel]
  );

  useAgentTweakable(ButtonTool, agentKey, false, onClick);

  return null;
}

export function CareStateScopeTree() {
  const { careState, scope, setScope, patientUserId } = useCareStateDomain();
  const commands = useCareStateCommands();
  const { actions: proposedActions } = useProposedActionsPlan();
  const pendingActionsCount = useMemo(
    () => proposedActions.filter(({ action }) => action.decision === 'Pending').length,
    [proposedActions]
  );

  const [isOpen, setIsOpen] = useState(true);
  const [requestTab, setRequestTab] = useState<RequestTabId>('active');
  const rows = useCareStateScopeRows(careState, requestTab);

  // First-in-render-order, regardless of urgency, gets the hint anchor.
  const firstRequestRowId = useMemo(() => {
    const firstRequestRow = rows.find((r) => r.id.startsWith('request:'));
    return firstRequestRow?.id ?? null;
  }, [rows]);

  const selection = useMemo(() => scopeToSelection(scope), [scope]);
  const selectionLabel = useMemo(() => {
    if (!selection) return '';
    return rows.find((r) => r.id === selection)?.label ?? '';
  }, [rows, selection]);

  useAgentVisible(TextFieldTool, 'currentCareScope', selection ?? '');
  useAgentVisible(TextFieldTool, 'currentCareScopeTitle', selectionLabel);

  const handleSelectRowId = useCallback(
    (rowId: string) => {
      const next = selectionToScope(rowId);
      if (next !== null) setScope(next);

      if (rowId.startsWith('request:')) {
        const requestId = rowId.slice('request:'.length);
        const request = careState?.requests.find((r) => r.id === requestId);
        const chatLink = request?.chat_link;
        if (chatLink?.type === 'chat_message') {
          openChatHistoryAtMessage({
            userId: patientUserId,
            conversationId: chatLink.conversation_id,
            messageId: chatLink.message_id,
          });
        }
      }
    },
    [setScope, careState, patientUserId]
  );

  const createProblem = useCallback(() => {
    const title = window.prompt('Problem title');
    if (title === null) return;
    commands.execute({ type: 'problem.create', title });
  }, [commands]);

  const renameProblem = useCallback(
    (problemId: string) => {
      const existing = careState?.problems.find((p) => p.id === problemId) ?? null;
      if (existing === null) return;
      const nextTitle = window.prompt('Problem title', existing.title);
      if (nextTitle === null) return;
      commands.execute({ type: 'problem.rename', problemId, title: nextTitle });
    },
    [careState, commands]
  );

  const deleteProblem = useCallback(
    (problemId: string) => {
      const ok = window.confirm('Delete problem?');
      if (!ok) return;
      commands.execute({ type: 'problem.delete', problemId });
    },
    [commands]
  );

  const createRequest = useCallback(
    (problemId: string) => {
      const title = window.prompt('Request title');
      if (title === null) return;
      commands.execute({ type: 'request.create', problemId, title });
    },
    [commands]
  );

  const renameRequest = useCallback(
    (requestId: string) => {
      const existing = careState?.requests.find((r) => r.id === requestId) ?? null;
      if (existing === null) return;
      const nextTitle = window.prompt('Request title', existing.title);
      if (nextTitle === null) return;
      commands.execute({ type: 'request.rename', requestId, title: nextTitle });
    },
    [careState, commands]
  );

  const deleteRequest = useCallback(
    (requestId: string) => {
      const ok = window.confirm('Delete request?');
      if (!ok) return;
      commands.execute({ type: 'request.delete', requestId });
    },
    [commands]
  );

  const setRequestStatus = (
    requestId: string,
    nextStatus: RequestStatusT,
    meta?: CareStateCommandMeta
  ) => {
    const nextOpenRequestId =
      nextStatus === 'Resolved' && careState !== null
        ? findNextOpenRequestInTreeOrder(careState, requestId)
        : null;

    commands.execute({ type: 'request.setStatus', requestId, status: nextStatus }, meta);

    if (nextStatus === 'Resolved') {
      if (nextOpenRequestId !== null) {
        setScope({ type: 'request', id: nextOpenRequestId });
      } else {
        setScope({ type: 'patient' });
      }
    }
  };

  const setRequestUrgency = (
    requestId: string,
    urgency: CareStateRequestUrgencyT,
    meta?: CareStateCommandMeta
  ) => {
    commands.execute({ type: 'request.setUrgency', requestId, urgency }, meta);
  };

  type ContextMenuItemDef =
    | { type: 'separator' }
    | {
        type: 'item';
        label: string;
        icon: React.ReactNode;
        action: () => void;
        variant?: 'destructive';
      };

  const isReadonly = ENV.isProduction;

  const buildContextMenuItems = useCallback(
    (row: ScopedListRow): ContextMenuItemDef[] => {
      if (row.id === 'patient:root') {
        if (isReadonly) return [];
        return [
          {
            type: 'item',
            label: 'Add Problem',
            icon: <Plus className="h-4 w-4" />,
            action: createProblem,
          },
        ];
      }

      if (row.id.startsWith('problem:')) {
        const problemId = row.id.slice('problem:'.length);
        const items: ContextMenuItemDef[] = [];
        if (!isReadonly) {
          items.push(
            {
              type: 'item',
              label: 'Add Request',
              icon: <Plus className="h-4 w-4" />,
              action: () => createRequest(problemId),
            },
            { type: 'separator' }
          );
        }
        items.push({
          type: 'item',
          label: 'Rename Problem',
          icon: <Pencil className="h-4 w-4" />,
          action: () => renameProblem(problemId),
        });
        if (!isReadonly) {
          items.push({
            type: 'item',
            label: 'Delete Problem',
            icon: <Trash2 className="h-4 w-4" />,
            action: () => deleteProblem(problemId),
            variant: 'destructive',
          });
        }
        return items;
      }

      if (row.id.startsWith('request:')) {
        const requestId = row.id.slice('request:'.length);
        const items: ContextMenuItemDef[] = [
          {
            type: 'item',
            label: 'Rename Request',
            icon: <Pencil className="h-4 w-4" />,
            action: () => renameRequest(requestId),
          },
        ];
        if (!isReadonly) {
          items.push({
            type: 'item',
            label: 'Delete Request',
            icon: <Trash2 className="h-4 w-4" />,
            action: () => deleteRequest(requestId),
            variant: 'destructive',
          });
        }
        return items;
      }

      return [];
    },
    [
      isReadonly,
      createProblem,
      createRequest,
      renameProblem,
      deleteProblem,
      renameRequest,
      deleteRequest,
    ]
  );

  const renderRowWrapper = useCallback(
    (row: ScopedListRow, children: React.ReactNode) => {
      const menuItems = buildContextMenuItems(row);
      if (menuItems.length === 0) return <>{children}</>;

      return (
        <ContextMenu>
          <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
          <ContextMenuContent>
            {menuItems.map((item, i) =>
              item.type === 'separator' ? (
                <ContextMenuSeparator key={i} />
              ) : (
                <ContextMenuItem key={item.label} onClick={item.action} variant={item.variant}>
                  {item.icon}
                  {item.label}
                </ContextMenuItem>
              )
            )}
          </ContextMenuContent>
        </ContextMenu>
      );
    },
    [buildContextMenuItems]
  );

  return (
    <AgentTweakableArea prefix="careStateScopeTree">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="rounded-t-md border border-b-0 bg-background/60 p-0.5 pb-3 backdrop-blur">
          <div className="mb-0.5 flex items-center justify-between gap-1.5">
            <RequestStatusTabs
              value={requestTab}
              onValueChange={setRequestTab}
              actionsCount={pendingActionsCount}
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full transition-colors',
                  requestTab === 'resolved'
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground/60 hover:bg-muted hover:text-muted-foreground'
                )}
                aria-label="Toggle resolved requests"
                onClick={() => setRequestTab(requestTab === 'resolved' ? 'active' : 'resolved')}
              >
                <History className="size-3" strokeWidth={2} />
              </button>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="rounded-full bg-muted px-1.5 py-px text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted/80"
                  aria-label={isOpen ? 'Collapse scope tree' : 'Expand scope tree'}
                >
                  {isOpen ? 'See Less' : 'See More'}
                </button>
              </CollapsibleTrigger>
            </div>
          </div>

          {/* Keep mounted for agent-tweakable registrations, but visually collapse via CSS */}
          <CollapsibleContent forceMount className={cn(!isOpen && 'hidden')}>
            {requestTab === 'actions' ? (
              <ProposedActionsPlanView
                emptyState={
                  <p className="px-2 py-4 text-center text-[11px] text-muted-foreground">
                    No actions yet. Copilot will suggest actions as you work through requests.
                  </p>
                }
              />
            ) : (
              <ScopedTreeViewer
                rows={rows}
                height={160}
                selection={selection}
                onSelectRowId={handleSelectRowId}
                renderMain={(row) => {
                  if (!row.id.startsWith('request:')) return null;
                  const requestId = row.id.slice('request:'.length);
                  const existing = careState?.requests.find((r) => r.id === requestId) ?? null;
                  if (existing === null) return null;

                  return (
                    <CareStateRequestRow
                      request={existing}
                      onSetStatus={setRequestStatus}
                      onSetUrgency={setRequestUrgency}
                      hintAnchor={
                        row.id === firstRequestRowId
                          ? CARE_STATE_FIRST_OPEN_REQUEST_ANCHOR
                          : undefined
                      }
                    />
                  );
                }}
                renderActions={(row) => {
                  const entityId = row.id.includes(':')
                    ? row.id.slice(row.id.indexOf(':') + 1)
                    : null;
                  const shortDate =
                    entityId && row.id !== 'patient:root'
                      ? formatLotusUuidShortDate(entityId)
                      : null;

                  return (
                    <>
                      <CareStateScopeTreeAgentRowButton
                        rowId={row.id}
                        rowLabel={row.label}
                        onSelectRowId={handleSelectRowId}
                      />
                      {shortDate ? (
                        <span className="shrink-0 text-[11px] font-semibold text-muted-foreground">
                          {shortDate}
                        </span>
                      ) : null}
                    </>
                  );
                }}
                renderRowWrapper={renderRowWrapper}
              />
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </AgentTweakableArea>
  );
}
